import { useEffect, useState } from "react";
import { editsMasterData, isDrinkProduct, isPantry, normalizeBarcode, pantryFieldsFromProduct, profileById } from "@lagerlog/core";
import type { ProductInfo } from "@lagerlog/core";
import { findArticles } from "../catalog.ts";
import type { Article, Catalog } from "../catalog.ts";
import { useStore } from "../hooks.ts";
import { failText } from "./EinkaufLink.tsx";
import { PantryForm } from "./Pantry.tsx";
import { Emoji } from "./Emoji.tsx";

type Answer = { product: ProductInfo | null; from: string | null; store?: boolean };

/**
 * A scanned code no article of the household has (docs/18-vorratsbuch.md, Strichcodes; F30):
 * what Open Food Facts knows about it (through the server), then „Als neuen Vorrat anlegen“ with
 * the form prefilled, or „Einem Vorrat zuordnen“. Either way the code stays at the article and
 * the article is chosen for booking. Owners and managers; others see what it is and who may. The
 * source and its licence stand once under Konto → Datenquellen, not at each product (Martin, 05.11.2026).
 */
export function UnknownCode({ code: raw, catalog, onChosen, onClose }: { code: string; catalog: Catalog; onChosen: (id: string) => void; onClose: () => void }) {
  const { store } = useStore();
  const code = normalizeBarcode(raw);
  const keeper = editsMasterData(store.rights().role);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [error, setError] = useState<string | null>(code ? null : `„${raw}“ hat nicht die Form eines Strichcodes (8, 12 oder 13 Ziffern mit Prüfziffer).`);
  const [mode, setMode] = useState<"choose" | "assign" | "create">("choose");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!code) return;
    let gone = false;
    store
      .request<Answer>(`/api/workspace/barcode/${code}`)
      .then((a) => !gone && setAnswer(a))
      .catch((e) => !gone && setError(failText(e)));
    return () => {
      gone = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const product = answer?.product ?? null;
  const drink = product ? isDrinkProduct(product) : false;
  const kept = code ?? raw;

  // What the card says, by case (Martin, 05.11.2026: „Unbekannter Strichcode“ misled when Open Food
  // Facts knows the product and only this household does not have it yet).
  const head = !code
    ? "Kein gültiger Strichcode"
    : error
      ? "Noch nicht in diesem Haushalt"
      : !answer
        ? `Strichcode ${kept}`
        : product
            ? "Noch nicht in diesem Haushalt"
            : answer.store
              ? "Code des Ladens"
              : "Strichcode nicht gefunden";
  const says = !code
    ? `${error} Prüf die Ziffern, oder leg den Vorrat ohne Code an.`
    : error
      ? `${error} Du kannst ihn trotzdem als neuen Vorrat anlegen oder einem vorhandenen zuordnen.`
      : !answer
        ? "Nicht in diesem Haushalt. Suche in Open Food Facts …"
        : product
            ? keeper
              ? "Open Food Facts kennt das Produkt. Leg es hier zum ersten Mal an, dann erkennt VorratsBuch den Code beim nächsten Scan."
              : "Open Food Facts kennt das Produkt, in diesem Haushalt ist es noch nicht angelegt."
            : answer.store
              ? `${kept} vergibt der Laden selbst (Waage, Eigenmarke); keine Datenbank kennt ihn. Du kannst ihn einem Vorrat zuordnen oder neu anlegen.`
              : `Weder dieser Haushalt noch die Datenbanken von Open Food Facts kennen ${kept}. Du kannst ihn trotzdem anlegen oder einem vorhandenen Vorrat zuordnen.`;
  // Not found, or not asked: look it up oneself. Both open in the person's browser; the server passes nothing on.
  const lookOut = Boolean(code) && ((answer !== null && !product && !answer.store) || error !== null);

  async function assign(a: Article) {
    await store.edit("article", a.id, { barcodes: [...a.barcodes.filter((b) => b !== kept), kept] });
    onChosen(a.id);
  }

  if (mode === "create") {
    const preset = product ? pantryFieldsFromProduct(product) : { profile: "lebensmittel" };
    const { barcodes: _, ...fields } = preset;
    return <PantryForm profile={profileById(String(fields.profile))} preset={fields} extra={{ barcodes: [kept] }} onDone={(id) => (id ? onChosen(id) : setMode("choose"))} />;
  }

  const hits = query.trim().length >= 2 ? findArticles([...catalog.articles.values()].filter((a) => isPantry(a.fields)), query).slice(0, 8) : [];

  return (
    <section className="card" data-testid="unknown-code">
      <strong data-testid="unknown-code-head">{head}</strong>
      {product && (
        <div className="row between" data-testid="unknown-code-product">
          <span className="named-emoji">
            <Emoji fields={pantryFieldsFromProduct(product)} />
            <span className="text">
              {product.brand ? `${product.brand.split(",")[0]} · ` : ""}
              {product.name}
            </span>
          </span>
          <span className="side">{product.quantity ?? ""}</span>
        </div>
      )}
      {says && (
        <p className={error && code ? "error-box" : "muted"} data-testid={answer && !product ? "unknown-code-none" : "unknown-code-says"}>
          {says}
        </p>
      )}
      {product && <p className="muted">Strichcode {kept}</p>}
      {/* A drink is taken like any food; only a hint at KellerLog (Martin, 05.11.2026: never refuse). */}
      {drink && (
        <p className="hint" data-testid="unknown-code-drink">
          Ein Getränk. Saft, Milch oder Wasser für den Haushalt gehören gut hierher; Wein, Bier und Spirituosen führst du besser in der App KellerLog.
        </p>
      )}
      {lookOut && (
        <div className="table-actions" data-testid="unknown-code-look">
          <a className="button" href={`https://duckduckgo.com/?q=${kept}`} target="_blank" rel="noreferrer" data-testid="unknown-code-web">
            Im Netz suchen
          </a>
          <a className="button" href={`https://world.openfoodfacts.org/product/${kept}`} target="_blank" rel="noreferrer" data-testid="unknown-code-off">
            Bei Open Food Facts eintragen
          </a>
        </div>
      )}
      {lookOut && <p className="hint">Wer es bei Open Food Facts einträgt, hilft allen: Spätestens nach einer Woche findet VorratsBuch es dann selbst.</p>}
      {!keeper && <p className="hint">Zuordnen und anlegen dürfen Inhaber und Verwaltung.</p>}
      {keeper && mode === "assign" && (
        <>
          <input autoFocus placeholder="Vorrat suchen" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="unknown-code-search" />
          <ul className="list" data-testid="unknown-code-hits">
            {hits.map((a) => (
              <li key={a.id}>
                <button className="row between" onClick={() => void assign(a)}>
                  <span className="named-emoji">
                    <Emoji fields={a.fields} />
                    <span className="text">{a.name}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="table-actions">
        {keeper && mode === "choose" && (
          <button className={product ? "primary" : ""} onClick={() => setMode("create")} data-testid="unknown-code-create">
            {product ? "Als neuen Vorrat anlegen" : "Neuen Vorrat anlegen"}
          </button>
        )}
        {keeper && mode === "choose" && (
          <button onClick={() => setMode("assign")} data-testid="unknown-code-assign">
            Einem vorhandenen Vorrat zuordnen
          </button>
        )}
        <button className="link" onClick={onClose} data-testid="unknown-code-close">
          Abbrechen
        </button>
      </div>
    </section>
  );
}
